/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { uatService } from '@/services/uat.service';
import { ApiError } from '@/services/api/types';
import { UatItemDto, UatFeedbackDto, UatProgressDto, UatOrgSummaryDto } from '@/types/uat.types';

export const uatKeys = {
  all: ['uat'] as const,
  items: () => [...uatKeys.all, 'items'] as const,
  feedback: (code: string) => [...uatKeys.all, 'feedback', code] as const,
  progress: () => [...uatKeys.all, 'progress'] as const,
  orgSummaries: () => [...uatKeys.all, 'orgSummaries'] as const,
  orgItems: (orgId: string) => [...uatKeys.all, 'orgItems', orgId] as const,
  orgFeedback: (orgId: string, code: string) => [...uatKeys.all, 'orgFeedback', orgId, code] as const,
};

// OrgAdmin hooks
export function useUatItems() {
  return useQuery<UatItemDto[], ApiError>({
    queryKey: uatKeys.items(),
    queryFn: uatService.getItems,
    staleTime: 30_000,
  });
}

export function useUatProgress() {
  return useQuery<UatProgressDto, ApiError>({
    queryKey: uatKeys.progress(),
    queryFn: uatService.getProgress,
    staleTime: 30_000,
  });
}

export function useUatFeedback(code: string) {
  return useQuery<UatFeedbackDto[], ApiError>({
    queryKey: uatKeys.feedback(code),
    queryFn: () => uatService.getFeedback(code),
    enabled: !!code,
  });
}

export function useUpdateUatStatus() {
  const qc = useQueryClient();
  return useMutation<UatItemDto, ApiError, { code: string; status: string }>({
    mutationFn: ({ code, status }) => uatService.updateStatus(code, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: uatKeys.items() });
      qc.invalidateQueries({ queryKey: uatKeys.progress() });
    },
  });
}

export function useAddUatFeedback() {
  const qc = useQueryClient();
  return useMutation<UatFeedbackDto, ApiError, { code: string; message: string; files?: File[] }>({
    mutationFn: ({ code, message, files }) => uatService.addFeedback(code, message, files),
    onSuccess: (_, { code }) => {
      qc.invalidateQueries({ queryKey: uatKeys.feedback(code) });
      qc.invalidateQueries({ queryKey: uatKeys.items() });
    },
  });
}

// SuperAdmin hooks
export function useUatOrgSummaries() {
  return useQuery<UatOrgSummaryDto[], ApiError>({
    queryKey: uatKeys.orgSummaries(),
    queryFn: uatService.getOrgSummaries,
    staleTime: 60_000,
  });
}

export function useUatOrgItems(orgId: string) {
  return useQuery<UatItemDto[], ApiError>({
    queryKey: uatKeys.orgItems(orgId),
    queryFn: () => uatService.getOrgItems(orgId),
    enabled: !!orgId,
  });
}

export function useUatOrgFeedback(orgId: string, code: string) {
  return useQuery<UatFeedbackDto[], ApiError>({
    queryKey: uatKeys.orgFeedback(orgId, code),
    queryFn: () => uatService.getOrgFeedback(orgId, code),
    enabled: !!orgId && !!code,
  });
}

export function useAddUatOrgFeedback() {
  const qc = useQueryClient();
  return useMutation<UatFeedbackDto, ApiError, { orgId: string; code: string; message: string; files?: File[] }>({
    mutationFn: ({ orgId, code, message, files }) => uatService.addOrgFeedback(orgId, code, message, files),
    onSuccess: (_, { orgId, code }) => {
      qc.invalidateQueries({ queryKey: uatKeys.orgFeedback(orgId, code) });
      qc.invalidateQueries({ queryKey: uatKeys.orgItems(orgId) });
    },
  });
}

export function useReviewUatItem() {
  const qc = useQueryClient();
  return useMutation<UatItemDto, ApiError, { orgId: string; code: string; status: string; note?: string }>({
    mutationFn: ({ orgId, code, status, note }) => uatService.reviewItem(orgId, code, status, note),
    onSuccess: (_, { orgId }) => {
      qc.invalidateQueries({ queryKey: uatKeys.orgItems(orgId) });
      qc.invalidateQueries({ queryKey: uatKeys.orgSummaries() });
    },
  });
}
